/**
 * The parameter registry.
 *
 * Morph exists because every other tool hardcodes presentation. A generator
 * that hardcodes presentation has rebuilt that problem one layer up — so every
 * number that influences what a layout looks like lives here, named, justified,
 * and classified by **what would be allowed to change it**:
 *
 *  - `invariant` — a floor that must never be generated, negotiated, or learned.
 *    Accessibility and legibility minimums. Hardcoding these is correct: it is
 *    the entire reason the runtime is the last line of defence. They are here to
 *    be *visible*, not to be tuned.
 *
 *  - `prior`     — a starting position held only until evidence exists. Every
 *    one names the evidence that replaces it. A prior that is still a prior
 *    after the data arrives is a bug, and `stillAGuess()` lists them.
 *
 *  - `derived`   — computed from the object shape at generation time. Not a
 *    constant at all; listed so the registry is a complete account of what
 *    decides a layout.
 *
 * The rule this enforces: **the system may hold an opinion about taste only as
 * a prior with a stated replacement path.** Anything asserting permanent taste
 * is a hardcoded design decision wearing a constant's clothing.
 */

export type ParamKind = 'invariant' | 'prior' | 'derived';

export interface Param<T> {
  readonly kind: ParamKind;
  readonly value: T;
  /** Why this number and not another. Blank is not acceptable. */
  readonly why: string;
  /** `prior` only: the evidence that retires it. */
  readonly replacedBy?: string;
}

const invariant = <T>(value: T, why: string): Param<T> => ({ kind: 'invariant', value, why });
const prior = <T>(value: T, why: string, replacedBy: string): Param<T> =>
  ({ kind: 'prior', value, why, replacedBy });

export const PARAMS = {
  /* ---------------------------------------------------------------- floors */

  /** Below this a field reads as *broken* rather than sparse, which is a
   *  legibility fact rather than a taste one. */
  roleCoverageFloor: invariant(0.35,
    'a field populated a third of the time reads as broken, not sparse'),

  /** A hero that is sometimes missing makes a grid go ragged — a structural
   *  failure, not a preference. */
  heroCoverageFloor: invariant(0.8,
    'an intermittent hero makes a grid ragged, which no taste setting redeems'),

  /** Beyond this, groups stop reading as groups and become noise. */
  groupableCardinalityMax: invariant(12,
    'twelve column headings is already a lot to scan; fifty is not grouping'),

  groupableCardinalityMin: invariant(2,
    'one group is not a grouping'),

  /**
   * Whether a lens may split into two regions around a view-state control.
   *
   * **OFF, and the reason is two measurements rather than a preference.**
   *
   * @Codex ruled that a slot is earned whenever the shape warrants one, and
   * built that way the control WORKS: one date split the films pool into 7
   * above and 5 at-or-below, from a single input, which is Coda's mechanism
   * running on a page for the first time.
   *
   * But an unset slot leaves both predicates inactive, and `not(absent)` is
   * absent — so until a person touches the control, region A shows everything
   * and region B shows everything:
   *
   *   1. the lens paints every object TWICE — 36 cards for 12 films;
   *   2. and its folds are then wrong. `smoke.mjs` measured *"folded 11,
   *      painted 22"*: the fold is computed over the lens scope while two
   *      regions each paint the whole of it, so a table foot states a total for
   *      half the rows above it. A correctness failure, not an aesthetic one.
   *
   * Neither is fixable in the spec, because *"collapse to one region while the
   * control is unset"* is conditional rendering the schema cannot express, and
   * doing it in the host would have the host reinterpret the spec — the thing
   * the ruling forbids.
   *
   * So the machinery stays and the emission waits for an answer. Everything
   * under it is built and guarded.
   *
   * **The first version of this comment ended "turning this on is one line once
   * the unset case has a design". That was wrong, and @Codex measured why.**
   * `baselineGenerator` is SHARED: five hosts in `apps/demo/src/main.ts` compile
   * against it and exactly one of them — the main stage — supplies `regionSets`.
   * `renderBacklinks` passes one projection as `objects` and nothing else, and
   * the renderer's fallback is deliberately silent for that case (`regionSets`
   * undefined means region addressing is OFF, not that a region is missing), so
   * a global flip would give the backlinks panel the double-paint bug in a
   * surface with no view-state control to fix it with — the exact failure this
   * flag is off to prevent, reintroduced where it is harder to see.
   *
   * So the unblocking work is per-host, not one line: every host compiling a
   * `cardinality: 'many'` shape must either implement per-region scopes AND
   * control state, or pass a shape/param pair that suppresses split generation.
   * Two of the four are already immune because they force `cardinality:
   * 'single'`, which `pickViewStateSlot` refuses outright.
   *
   * **@Codex RULED on the unset case, 2026-08-26, and rejected the host-side
   * answer.** Decision 0020 proposed `repeatedWhileUnset` — hold back a region
   * whose only distinguishing predicate reads an unset slot — argued as the
   * deduplication `answerSubjectRegionScopes` already performs. The ruling
   * draws the line exactly where the argument was weakest:
   *
   * > *`answerSubjectRegionScopes` ... caches one store answer for identical
   * > scope keys but still assigns that answer to EVERY DECLARED REGION. It
   * > changes the number of reads, not the structure or meaning of the lens.*
   *
   * Removing a requester is not sharing an answer among requesters, and two
   * regions can ask one question while differing in placement, labelling or
   * item layout. So the unset presentation must be DECLARED in the spec — a
   * validated partition relation naming its branches by node id, its
   * controlling slot, and which branch stands alone while the slot is unset —
   * and the host may execute that declaration but never infer it. Task 0095.
   *
   * The distinct-value floor the ruling names — three, because *"two values
   * describe a binary category wearing a numeric encoding"* — is inline in
   * `pickViewStateSlot` rather than a parameter here, because a parameter
   * behind a switch that ships off reaches no shipped layout, and this
   * registry's own test refuses a parameter that cannot move one. It becomes a
   * parameter when the split does.
   */
  splitOnViewState: invariant(false,
    // The DEFAULT stays off, and the stage opts in through `stageGenerator`
    // with `STAGE_SPLITS_ON_VIEW_STATE = true` — live since the declared
    // partition landed and `e2e/partition.mjs` drove it in a browser. Off here
    // because `baselineGenerator` is shared by five hosts and only the stage
    // supplies `regionSets`; a global default would give the double paint to
    // `renderBacklinks`, which has no control to fix it with.
    'only the host that supplies regionSets may split; the stage opts in'),

  /* ---------------------------------------------------------------- priors */

  /** Cold-start expectation of information appetite per context, on 0..1 where
   *  1 is maximally dense.
   *
   *  **This is the file's most dangerous entry.** It is a claim about taste, and
   *  taste is exactly what Morph refuses to hardcode. It survives only as a
   *  prior with weight — see `priorMass` — so four signals from a real user
   *  overrule it, and the pool overrules it for everyone once it has data. */
  contextDensityAppetite: prior(
    { reading: 0.35, authoring: 0.45, browsing: 0.2, studying: 0.15, comparing: 0.85 },
    'a first guess at how much someone wants on screen per activity, nothing more',
    'the pool posterior for the matching (context, cardinality, mediaWeight) cell, once ≥50 genomes exist',
  ),

  /** How strongly a cold-start prior is held, in units where one keep = 1. */
  priorMass: prior(4,
    'four consistent signals from a real user should overrule any guess we ship',
    'measured acceptance rates; if users override sooner, this is too high',
  ),

  /** Metadata slots offered before the density appetite starts suppressing. */
  metadataBudgetBase: prior(3,
    'three secondary fields is a legible card at ordinary density',
    'observed hide/reveal adjustments — the discovery channel, not keep/dismiss',
  ),

  /** Relation density above which a single-object lens offers relations. */
  relationSurfaceThreshold: prior(0.2,
    'below this, a relations region is usually empty and reads as broken',
    'observed engagement with relation regions when offered',
  ),

  /** Cross-document relation density above which relations are offered
   *  regardless of `relationSurfaceThreshold`. */
  crossDocSurfaceThreshold: prior(0,
    'a reference the lens does not show is invisible unless it is drawn, so any '
    + 'is enough — the threshold above exists to stop REPETITION, and there is '
    + 'none to stop when the target is in another document',
    'observed engagement with cross-document references when offered',
  ),
} as const;

export type ParamName = keyof typeof PARAMS;

/** Runtime overrides, so nothing here is baked into a build. Every constant
 *  must be tunable per cohort from the first build — a parameter you cannot
 *  move to test it is indistinguishable from one you hardcoded. */
export type ParamOverrides = Partial<{ [K in ParamName]: (typeof PARAMS)[K]['value'] }>;

export function resolveParams(overrides: ParamOverrides = {}) {
  const out = {} as { [K in ParamName]: (typeof PARAMS)[K]['value'] };
  for (const key of Object.keys(PARAMS) as ParamName[]) {
    out[key] = (overrides[key] ?? PARAMS[key].value) as never;
  }
  return out;
}

export type ResolvedParams = ReturnType<typeof resolveParams>;

/** Every prior still standing on its shipped guess. This list should shrink as
 *  the product learns; if it doesn't, the product isn't learning. */
export function stillAGuess(): Array<{ name: ParamName; why: string; replacedBy: string }> {
  return (Object.keys(PARAMS) as ParamName[])
    .filter((k) => PARAMS[k].kind === 'prior')
    .map((k) => ({
      name: k,
      why: PARAMS[k].why,
      replacedBy: (PARAMS[k] as { replacedBy: string }).replacedBy,
    }));
}

/** Audit hook: assert nothing claims permanent taste. An invariant must be a
 *  floor — something that stops a layout being unusable — never a preference
 *  about how a usable layout should look. */
export function auditRegistry(): string[] {
  const problems: string[] = [];
  for (const key of Object.keys(PARAMS) as ParamName[]) {
    const p = PARAMS[key];
    if (!p.why || p.why.length < 10) problems.push(`${key}: missing justification`);
    if (p.kind === 'prior' && !('replacedBy' in p && p.replacedBy)) {
      problems.push(`${key}: a prior with no path to being replaced is a hardcoded opinion`);
    }
    if (p.kind === 'invariant' && 'replacedBy' in p) {
      problems.push(`${key}: an invariant that can be replaced is not an invariant`);
    }
  }
  return problems;
}
